/**
 * BrawlConfig — single visible toggle surface for the /brawl URL.
 *
 * DJ's canonical test surface is `https://h2000-ptr.djmsqrvve.com/brawl`.
 * Every knob that drives brawl behavior lives in this file. Edit values
 * here to change what the URL does — no need to touch BrawlArena.ts,
 * main.tsx, or scattered presets.
 *
 * Wiring:
 *   - main.tsx               → reads on /brawl path mount; injects URL
 *                              params + triggers cookie/storage wipe
 *   - BrawlArena.ts          → reads to populate metadata.{pvp,bot}
 *                              (baked at server module init)
 *   - GameRoom               → indirectly via resolveBotConfig() reading
 *                              the metadata produced above
 *
 * See ~/.claude/projects/-home-dj-dev-helix-Helix2000/memory/feedback_brawl_url_canon.md
 */

export const BRAWL_CONFIG = {
  // ---------------------------------------------------------------------------
  // Cookie/cache hygiene — DJ's "no private window dance" requirement.
  // Wipes Twitch session cookie + helix_* localStorage on first /brawl
  // navigation per tab. Subsequent F5 reloads in the same tab skip the
  // wipe (per-tab marker guards against an infinite reset→reload loop).
  // Open a new tab or close+reopen for a fresh wipe.
  // ---------------------------------------------------------------------------
  resetOnLoad: true,

  // ---------------------------------------------------------------------------
  // Class defaults — what DJ spawns into.
  // ---------------------------------------------------------------------------
  defaultClass: 'mage' as const,

  /**
   * Bot roster — array drives botCount + botClasses + per-bot tier + name +
   * ABSOLUTE spawn position (`tileX` = tiles from the left wall; arena is 200
   * wide, floor row 53). Fixed coords so bots don't "move" between sessions.
   *
   * CRAZY FREE-FOR-ALL (2026-06-04): /brawl is the chaotic PvP surface it was
   * meant to be — a pile of `aggressive` bots across all 5 classes that chase +
   * fight each other AND DJ AND other humans (botBehavior='attack-all'). The
   * deliberate, structured multi-level PvP testing lives on /arena (ArenaConfig).
   */
  botRoster: [
    { class: 'warrior',  tier: 'aggressive', name: 'Reaver',     tileX: 35 },
    { class: 'mage',     tier: 'aggressive', name: 'Pyromancer', tileX: 65 },
    { class: 'hunter',   tier: 'aggressive', name: 'Stalker',    tileX: 95 },
    { class: 'paladin',  tier: 'aggressive', name: 'Crusader',   tileX: 120 },
    { class: 'rogue', tier: 'aggressive', name: 'Shade',      tileX: 150 },
    { class: 'mage',     tier: 'aggressive', name: 'Tempest',    tileX: 175 },
  ] as const,

  // ---------------------------------------------------------------------------
  // PvP knobs — flow into BrawlArena.metadata.pvp at module init.
  // friendlyFire='all' lets DJ poke the bot AND, in multi-player, other
  // humans. botBehavior='attack-all' makes the bot fight back.
  // ---------------------------------------------------------------------------
  pvpEnabled: true,
  friendlyFire: 'all' as 'all' | 'players-only' | 'off',
  botBehavior: 'attack-all' as 'attack-all' | 'attack-non-owner' | 'ignore',
  respawnDelayMs: 1200,
  respawnInvulnerabilityMs: 800,

  // ---------------------------------------------------------------------------
  // HUD overrides — what /brawl wants visible.
  // - preset feeds devConfig's preset resolver (live-lobby = clean
  //   social-hub viewport with all dev UI off, plus nameplates/HP bars on)
  // - skipSplash/skipLogin auto-skip the click-through screens
  // ---------------------------------------------------------------------------
  preset: 'live-lobby' as const,
  skipSplash: true,
  skipLogin: true,
} as const;
